import React, { useEffect, useState, useCallback } from "react";
import {
	Question,
	QuestionRow,
	QuestionTable,
	QuestionTableBodyRow,
	QuestionTableHeaderRow,
	QuestionTableFooterRow,
	QuestionTableCell,
	QuestionTableRowDeleteButton,
} from "./Question";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import {
	selectSession,
	makeSelectLookupByDomainCode,
	makeSelectTreeByDomainCode,
	makeSelectChildQuestions,
	makeSelectRowsByQuestionId,
	selectValueByQuestionId,
	makeSelectFormulaByRowId,
	makeSelectAttachmentsByQuestionId,
	selectTotalFundingDifference,
} from "reducers";
import { newId } from "utils/common";
import { answerUpserted } from "reducers/answersSlice";
import {
	fetchAttachmentById,
	attachmentUpserted,
} from "reducers/attachmentsSlice";
import {
	deleteRowAndAnswers,
	rowAdded,
	rowStatusEdited,
	selectSequence,
} from "reducers/rowsSlice";
import { validator } from "utils/common";
import {
	selectFormState,
	postForm,
	selectFormId,
	selectFormStateInitialisedRows,
	rowInitialised,
} from "reducers/formSlice";
import { getPropertyCi } from "utils/common";

import {
	TotalCost,
	TotalOngoing,
	TotalNbv,
} from "features/capex/DataComponents";

/*
Container for business logic of how to render questions
and the state selectors/dispatchers
*/

const defaultFormState = { isNew: true, isLocked: false };
const defaultAnswer = { value: undefined };
const initialState = [];

const getFormulaValueIfExists = (
	expression,
	formulaId,
	formulaValue,
	defaultValue = null
) => {
	//if a formula is present
	if (typeof expression === "object" && expression !== null) {
		//return value for formula if exists
		if (formulaId && formulaValue) {
			return formulaValue;
		}
		//otherwise return null - since formula value doesn't exist
		else return null;
	}
	//if not formula, return the default value
	else return expression;
};

const QuestionChild = ({ question, instanceId, isTable, row }) => {
	const { validation, defaultValue, visible, domain, disabled } = question;
	const isMandatory = validation?.some((v) => v.type === "mandatory");

	//#region redux selectors for state to pass to Question component
	const totalFundingDifference = useSelector(selectTotalFundingDifference);
	const { value: standardValue } =
		useSelector(
			(state) =>
				selectValueByQuestionId(state, instanceId || question.id),
			shallowEqual
		) || defaultAnswer;

	const value =
		question.id === "totalFundingDifference"
			? totalFundingDifference
			: standardValue;

	const selectLookupByDomainCode = makeSelectLookupByDomainCode(domain);
	const lookup = useSelector(selectLookupByDomainCode);

	const selectTreeByDomainCode = makeSelectTreeByDomainCode(domain);
	const tree = useSelector(selectTreeByDomainCode);

	const selectAttachmentById = makeSelectAttachmentsByQuestionId(
		instanceId || question.id
	);

	const formId = useSelector(selectFormId);

	const attachments = useSelector(selectAttachmentById);

	//global lock status
	const { isLocked, isNew: isFormNew } =
		useSelector(selectFormState) || defaultFormState;

	//#endregion

	//#region evaluate formulas for visible and defaultValue
	/*
	try to get calculatedValue from defaultValue 
	sometimes it is a primitive value, and sometimes
	it is an object containing a formula
	*/
	const formulaId = defaultValue?.formulaId;
	const selectFormulaValueById = makeSelectFormulaByRowId(row?.id);
	const formulaValue = useSelector(
		(state) => selectFormulaValueById(state, formulaId),
		shallowEqual
	);

	const calculatedValue = getFormulaValueIfExists(
		defaultValue,
		formulaId,
		formulaValue,
		question?.defaultValue
	);

	const visibleFormulaId = visible?.formulaId;
	const selectVisibleFormulaValueById = makeSelectFormulaByRowId(row?.id);
	const visibleFormulaValue = useSelector(
		(state) => selectVisibleFormulaValueById(state, visibleFormulaId),
		shallowEqual
	);

	const calculatedVisibility = getFormulaValueIfExists(
		visible || true,
		visibleFormulaId,
		visibleFormulaValue
	);
	//#endregion

	//#region validation logic
	const { status } = useSelector(selectSession);
	const [errors, setErrors] = useState(initialState);
	useEffect(() => {
		//on change of value, re-run validation
		if (validation && validation.length > 0) {
			const errors = validation.reduce((acc, v) => {
				let result = validator(v, value);
				if (result) acc.push(result);
				return acc;
			}, []);
			setErrors(errors && errors.length > 0 ? errors : initialState);
		}
	}, [value, validation]);
	//#endregion

	//#region  dispatchers
	const dispatch = useDispatch();
	const answerAddedDispatcher = useCallback(
		(action) => dispatch(answerUpserted(action)),
		[dispatch]
	);

	const answerEditedDispatcher = useCallback(
		(action) => dispatch(answerUpserted(action)),
		[dispatch]
	);

	const attachmentDispatcher = useCallback(
		(action) => dispatch(attachmentUpserted(action)),
		[dispatch]
	);

	const fetchAttachmentByIdDispatcher = useCallback(
		(action) => dispatch(fetchAttachmentById(action)),
		[dispatch]
	);

	//input types that deal with child records must save the form first
	const saveFormDispatcher = useCallback(
		async () => dispatch(postForm()),
		[dispatch]
	);
	//#endregion

	return calculatedVisibility ? (
		isTable ? (
			<Question
				question={question}
				status={status}
				dispatch={[answerAddedDispatcher, answerEditedDispatcher]}
				attachmentDispatch={[
					attachmentDispatcher,
					fetchAttachmentByIdDispatcher,
				]}
				lookup={lookup}
				value={value}
				defaultValue={calculatedValue}
				visible={calculatedVisibility}
				isNew={!value}
				tree={tree}
				errors={errors}
				disabled={disabled || isLocked}
				attachments={attachments}
				instanceId={instanceId}
				row={row}
				isFormNew={isFormNew}
				saveFormDispatch={saveFormDispatcher}
				formId={formId}
			/>
		) : (
			<QuestionRow question={question} isMandatory={isMandatory}>
				<Question
					question={question}
					status={status}
					dispatch={[answerAddedDispatcher, answerEditedDispatcher]}
					attachmentDispatch={[
						attachmentDispatcher,
						fetchAttachmentByIdDispatcher,
					]}
					lookup={lookup}
					value={value}
					defaultValue={calculatedValue}
					visible={calculatedVisibility}
					isNew={!value}
					tree={tree}
					errors={errors}
					disabled={disabled || isLocked}
					attachments={attachments}
					isFormNew={isFormNew}
					saveFormDispatch={saveFormDispatcher}
					formId={formId}
				/>
			</QuestionRow>
		)
	) : null;
};

const QuestionParent = ({ question }) => {
	const { id } = question;
	const { isLocked } = useSelector(selectFormState);

	//#region child questions for table-based questions
	const selectChildQuestions = makeSelectChildQuestions(id);

	const childQuestions = useSelector(
		(state) => selectChildQuestions(state, id),
		shallowEqual
	);
	const selectRowsByQuestionId = makeSelectRowsByQuestionId(id);

	const rows = useSelector(selectRowsByQuestionId, shallowEqual);

	const initialisedRows = useSelector(selectFormStateInitialisedRows);
	//#endregion

	//#region  dispatchers
	const dispatch = useDispatch();
	const newSequence = useSelector(selectSequence) + 1;
	const addNewRow = React.useCallback(() => {
		let data = {
			id: newId(),
			questionId: question.id,
			sequence: newSequence,
		};

		//columns (child questions)
		childQuestions.forEach((q) => {
			//map through each column and assign
			//1. the property name to match the question id of the child
			//2. the property value to be the ID of the value later to be added
			data[q.id] = newId();
		});
		dispatch(rowAdded(data));
	}, [childQuestions, question.id, dispatch, newSequence]);
	//#endregion dispatchers

	React.useEffect(() => {
		//when a form is first started, we initialise the tables with an empty row
		//important to NOT do this later while navigating pages or while other people are approving the form
		if (rows.length === 0 && !initialisedRows.includes(question.id)) {
			addNewRow();
			dispatch(rowInitialised(question.id));
			dispatch(rowStatusEdited("idle"));
		}
	}, [rows, dispatch, addNewRow, initialisedRows, question.id]);

	//#region Calculated visibility
	const { visible } = question;
	const visibleFormulaId = visible?.formulaId;
	const selectVisibleFormulaValueById = makeSelectFormulaByRowId(null);
	const visibleFormulaValue = useSelector(
		(state) => selectVisibleFormulaValueById(state, visibleFormulaId),
		shallowEqual
	);

	const calculatedVisibility = getFormulaValueIfExists(
		visible || true,
		visibleFormulaId,
		visibleFormulaValue
	);
	//#endregion

	return (
		calculatedVisibility && (
			<QuestionTable question={question}>
				<QuestionTableHeaderRow questions={childQuestions} />
				{rows &&
					rows.length > 0 &&
					rows.map((row) => {
						return (
							<QuestionTableBodyRow key={row.id}>
								{childQuestions.map((q, index) => {
									let instanceId = getPropertyCi(row, q.id);
									return (
										<QuestionTableCell
											key={index}
											span={q.col.span}
										>
											<QuestionChild
												question={q}
												instanceId={instanceId}
												isTable={true}
												row={row}
											/>
										</QuestionTableCell>
									);
								})}
								{!isLocked && (
									<QuestionTableRowDeleteButton
										onDelete={() => {
											dispatch(deleteRowAndAnswers(row));
										}}
									/>
								)}
							</QuestionTableBodyRow>
						);
					})}
				{(question.id === "q1" ||
					question.id === "q8" ||
					question.id === "q10") && (
					<QuestionTableBodyRow>
						{childQuestions.map((q, index) => {
							return (
								<QuestionTableCell
									key={index}
									className="qna-question-table-total-cell"
									span={q.col.span}
								>
									{q.id === "q7" ? (
										<TotalCost />
									) : q.id === "totalOngoingCost" ? (
										<TotalOngoing />
									) : q.id === "NBV" ? (
										<TotalNbv />
									) : (
										<span>&nbsp;</span>
									)}
								</QuestionTableCell>
							);
						})}
					</QuestionTableBodyRow>
				)}

				{!isLocked && (
					<QuestionTableFooterRow
						question={question}
						childQuestions={childQuestions}
						addNewRow={addNewRow}
					/>
				)}
			</QuestionTable>
		)
	);
};

const QuestionContainer = ({ question, instanceId }) => {
	return question.inputType === "Table" ? (
		<QuestionParent question={question}></QuestionParent>
	) : (
		<QuestionChild question={question} instanceId={instanceId} />
	);
};

export default QuestionContainer;
